import { Box } from "@mui/material"
import { type FC } from "react"
import { useFormContext } from "react-hook-form"

import type { GoodsReceiptFormValues } from "../../schemas"

import AutoComplete from "@/components/common/Autocomplete/AutoComplete"
import { FormDatePicker, FormTextField } from "@/components/UiKit"
import { DEFAULT_AUTOCOMPLETE_VALUE } from "@/constants/forms"
import { useLookupWarehouses } from "@/hooks/useLookupWarehouses"
import { TrackingNumbersFields } from "@/screens/WarehouseReceiving/components"
import type { AutocompleteOption } from "@/types/form-fields"
import { getAutoCompleteOptionLabel } from "@/utils/forms"

interface GoodsReceiptFormProps {
  hasPurchaseOrder?: boolean
  onSupplierSearch?: (searchTerm: string) => void
  supplierOptions?: AutocompleteOption<string>[]
  isLoadingSuppliers?: boolean
  isDeliveryLocationDisabled?: boolean
}

export const GoodsReceiptForm: FC<GoodsReceiptFormProps> = ({
  hasPurchaseOrder = false,
  onSupplierSearch,
  supplierOptions = [],
  isLoadingSuppliers = false,
  isDeliveryLocationDisabled = false,
}) => {
  const { control } = useFormContext<GoodsReceiptFormValues>()
  const { warehouses, isFetching: isFetchingWarehouses } = useLookupWarehouses()

  return (
    <Box className='space-y-6'>
      <TrackingNumbersFields />

      <FormDatePicker
        control={control}
        name='estimatedDeliveryOn'
        label='Estimated Delivery Date'
        data-testid='estimated-delivery-date-picker'
      />

      <AutoComplete<AutocompleteOption<string>>
        label='Delivery Location'
        placeholder='Select warehouse'
        name='deliveryLocation'
        id='delivery-location-autocomplete'
        options={warehouses}
        getOptionLabel={getAutoCompleteOptionLabel}
        control={control}
        clearOnEscape={true}
        defaultEmptyState={DEFAULT_AUTOCOMPLETE_VALUE}
        loading={isFetchingWarehouses}
        required
        disabled={isDeliveryLocationDisabled}
        data-testid='delivery-location-autocomplete'
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />

      {!hasPurchaseOrder && (
        <>
          <FormTextField
            name='estimatedPallets'
            control={control}
            label='Estimated Pallets'
            fullWidth
            type='number'
            placeholder='Enter estimated pallets'
            variant='outlined'
            slotProps={{ htmlInput: { min: 1 } }}
            data-testid='estimated-pallets-input'
          />
          <AutoComplete<AutocompleteOption<string>>
            label='Supplier'
            placeholder='Search and select supplier'
            name='supplier'
            id='supplier-autocomplete'
            options={supplierOptions}
            getOptionLabel={getAutoCompleteOptionLabel}
            control={control}
            clearOnEscape={true}
            defaultEmptyState={DEFAULT_AUTOCOMPLETE_VALUE}
            loading={isLoadingSuppliers}
            onInputChange={onSupplierSearch}
            freeSolo={false}
            data-testid='supplier-autocomplete'
          />
        </>
      )}
    </Box>
  )
}
